// src/components/homepage/blog.tsx
"use client";
import React, { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation, Autoplay } from "swiper/modules";
import Link from "next/link";
import Image from "next/image";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";

interface Post {
    slug: { _type: string; current: string };
    thumbImage: { asset: { url: string } };
    title: string;
}

interface BlogCarouselProps {
    posts: Post[];
}

export default function BlogCarousel({ posts }: BlogCarouselProps) {
    const swiperConfig = useMemo(
        () => ({
            loop: posts.length > 1,
            breakpoints: {
                320: { slidesPerView: 2, spaceBetween: 5 },
                640: { slidesPerView: 2, spaceBetween: 5 },
                768: { slidesPerView: 2, spaceBetween: 5 },
                1024: { slidesPerView: 3, spaceBetween: 5 },
            },
            autoplay: { delay: 2500, disableOnInteraction: false },
            effect: "coverflow" as const,
            centeredSlides: true,
            coverflowEffect: {
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
            },
            navigation: true,
            modules: [EffectCoverflow, Navigation, Autoplay],
        }),
        [posts.length]
    );

    if (posts.length === 0) {
        return (
            <p className="text-center text-white">
                No blog posts available at the moment.
            </p>
        );
    }

    return (
        <Swiper {...swiperConfig}>
            {posts.map((feature, index) => (
                <SwiperSlide
                    key={`${index}-Slider`}
                    className="relative swiper-slide p-0 sm:p-4 pt-4"
                >
                    <Link href={`/digital-gold-blog/${feature.slug.current}`} passHref>
                        <div className="border border-gray-200 rounded-b-lg rounded-t-xl pb-1 hover:shadow-lg transition-shadow">
                            <div className="relative max-w-[640px] h-auto overflow-hidden rounded-t-xl mx-auto">
                                <Image
                                    src={feature.thumbImage?.asset?.url}
                                    alt={feature.title}
                                    width={640} // Max width, height adjusts automatically
                                    height={0} // Let intrinsic height determine size
                                    className="w-full h-auto object-contain rounded-t-xl cursor-pointer"
                                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                                    loading={index === 0 ? "eager" : "lazy"}
                                />
                            </div>
                            <p className="text-white text-center text-lg font-poppins line-clamp-1 sm:line-clamp-2 px-2 py-1">
                                {feature.title}
                            </p>
                        </div>
                    </Link>
                </SwiperSlide>
            ))}
        </Swiper>
    );
}